import React, { useState, useEffect, useCallback } from 'react';

const TIER_LABELS = {
  mcqPoints:      { label: 'MCQ',              max: 12000, color: 'bg-blue-400'   },
  guided4Points:  { label: 'Guided 4-mark',    max: 2500,  color: 'bg-slate-400'  },
  guided6Points:  { label: 'Guided 6-mark',    max: 7500,  color: 'bg-sky-400'    },
  guided9Points:  { label: 'Guided 9-mark',    max: 8000,  color: 'bg-violet-400' },
  guided20Points: { label: 'Guided 20-mark',   max: 9000,  color: 'bg-rose-400'   },
  mock4Points:    { label: 'Mock 4-mark',       max: 400,   color: 'bg-amber-400'  },
  mock6Points:    { label: 'Mock 6-mark',       max: 2400,  color: 'bg-orange-400' },
  mock9Points:    { label: 'Mock 9-mark',       max: 1600,  color: 'bg-red-400'    },
  mock20Points:   { label: 'Mock 20-mark',      max: 2400,  color: 'bg-pink-400'   },
  timePoints:     { label: 'Time & Engagement', max: 7000,  color: 'bg-green-400'  },
};

const STREAK_ICONS = [
  { min: 28, icon: '🔥🔥🔥', label: 'Monthly Master' },
  { min: 14, icon: '🔥🔥',   label: 'Fortnight Focus' },
  { min: 7,  icon: '🔥',     label: 'Weekly Warrior'  },
  { min: 3,  icon: '✨',     label: 'Hat Trick'       },
];

function StreakBadge({ days }) {
  if (days < 3) return null;
  const tier = STREAK_ICONS.find(t => days >= t.min);
  return (
    <span className="inline-flex items-center gap-1 text-xs" title={`${days}-day streak`}>
      <span>{tier?.icon}</span>
      <span className="text-[#d29922] font-mono text-[10px]">{days}d</span>
    </span>
  );
}

function ProgressBar({ value, max, color = 'bg-blue-400', label, showNumbers = true }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-[10px] text-[#8b949e] font-mono w-28 flex-shrink-0">{label}</span>}
      <div className="flex-1 h-1.5 bg-[#21262d] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {showNumbers && (
        <span className="text-[10px] text-[#8b949e] font-mono w-20 text-right flex-shrink-0">
          {value.toLocaleString()} / {max.toLocaleString()}
        </span>
      )}
    </div>
  );
}

export default function Leaderboard({ onClose, getAccessToken, isAdmin, onStudentClick }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [lbRes, summaryRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/leaderboard`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/api/points/summary`, { headers }),
      ]);
      const lbData = await lbRes.json();
      const summaryData = await summaryRes.json();
      setLeaderboard(lbData.leaderboard || []);
      setSummary(summaryData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d1117]">
        <div className="text-[#8b949e] text-sm font-mono animate-pulse">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d1117] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[#e6edf3] text-lg font-semibold">Leaderboard</h2>
          <p className="text-[#8b949e] text-xs font-mono mt-1">Summer Term 2026</p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#161b22] transition-colors"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {error && <div className="text-red-400 text-xs mb-4">Error: {error}</div>}

      {/* Your Score Summary */}
      {summary && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-[#e6edf3] text-2xl font-bold font-mono">
                {((summary.totalPoints || 0) + (summary.bonusPoints || 0)).toLocaleString()}
              </span>
              <span className="text-[#8b949e] text-xs ml-2">/ 50,000 pts</span>
            </div>
            <StreakBadge days={summary.currentStreakDays || 0} />
          </div>

          {/* Pace indicator */}
          <div className="flex items-center gap-2 mb-4">
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
              summary.paceStatus === 'ahead' ? 'bg-[#3fb950]/10 text-[#3fb950]' :
              summary.paceStatus === 'on_track' ? 'bg-[#d29922]/10 text-[#d29922]' :
              'bg-[#f85149]/10 text-[#f85149]'
            }`}>
              {summary.paceStatus === 'ahead' ? 'Ahead of target' :
               summary.paceStatus === 'on_track' ? 'On track' : 'Behind target'}
            </span>
            <span className="text-[10px] text-[#8b949e] font-mono">
              Target: {(summary.weeklyTarget || 10000).toLocaleString()}/week
            </span>
          </div>

          {/* Tier breakdown */}
          <div className="space-y-1.5">
            {Object.entries(TIER_LABELS).map(([key, cfg]) => (
              <ProgressBar
                key={key}
                value={summary[key] || 0}
                max={cfg.max}
                color={cfg.color}
                label={cfg.label}
              />
            ))}
          </div>

          {/* Next unlock */}
          {summary.nextUnlocks?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#30363d]">
              <p className="text-[10px] text-[#8b949e] font-mono uppercase tracking-wider mb-1">Next unlock</p>
              {summary.nextUnlocks.slice(0, 2).map((u, i) => (
                <div key={i} className="flex items-center justify-between text-xs mt-1">
                  <span className="text-[#e6edf3]">{u.tier.replace(/_/g, ' ')}</span>
                  <span className="text-[#8b949e] font-mono">
                    {u.progress} / {u.target} {u.requirement}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Table */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[3rem_1fr_6rem_5rem_4rem] gap-2 px-4 py-2 border-b border-[#30363d] text-[10px] text-[#8b949e] font-mono uppercase tracking-wider">
          <span>Rank</span>
          <span>Student</span>
          <span className="text-right">Total</span>
          <span className="text-right">Today</span>
          <span className="text-right">Streak</span>
        </div>
        {leaderboard.length === 0 ? (
          <div className="px-4 py-8 text-center text-[#484f58] text-xs italic">
            No scores yet this term. Start earning points!
          </div>
        ) : (
          leaderboard.map((row) => (
            <div
              key={row.rank}
              className={`grid grid-cols-[3rem_1fr_6rem_5rem_4rem] gap-2 px-4 py-2.5 border-b border-[#21262d] text-xs transition-colors ${
                row.isCurrentUser
                  ? 'bg-[#58a6ff]/5 border-l-2 border-l-[#58a6ff]'
                  : 'hover:bg-[#21262d]'
              }`}
            >
              <span className={`font-mono font-bold ${
                row.rank === 1 ? 'text-[#d29922]' :
                row.rank === 2 ? 'text-[#8b949e]' :
                row.rank === 3 ? 'text-[#da7a39]' :
                'text-[#484f58]'
              }`}>
                #{row.rank}
              </span>
              {isAdmin && !row.isCurrentUser ? (
                <button
                  onClick={() => onStudentClick({ userId: row.userId, displayName: row.displayName })}
                  className="truncate text-left text-[#e6edf3] hover:text-[#58a6ff] hover:underline transition-colors"
                >
                  {row.displayName}
                </button>
              ) : (
                <span className={`truncate ${row.isCurrentUser ? 'text-[#58a6ff] font-semibold' : 'text-[#e6edf3]'}`}>
                  {row.displayName}
                  {row.isCurrentUser && <span className="text-[#58a6ff]/60 ml-1">(you)</span>}
                </span>
              )}
              <span className="text-right font-mono text-[#e6edf3]">
                {row.totalPoints.toLocaleString()}
              </span>
              <span className={`text-right font-mono ${
                (row.todayPoints ?? 0) > 0 ? 'text-[#3fb950]' : 'text-[#484f58]'
              }`}>
                {(row.todayPoints ?? 0).toLocaleString()}
              </span>
              <span className="text-right">
                <StreakBadge days={row.currentStreak} />
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
